import React from "react";
import { useTranslation } from "react-i18next";
import "./HeroTelealarmeISS.css";

/**
 * Téléalarme ISS — Hero premium 01.
 *
 * Le composant conserve son nom et son export ; aucune modification
 * nécessaire dans TelealarmeISS.jsx.
 *
 * Le PNG du matériel existe déjà sur le site : public/images/Tele_part.png.
 * La maquette fournie n'est pas utilisée comme photographie de produit.
 */
const PRODUCT_IMAGE = "/images/Tele_part.png";

// La route /devis présente dans l'ancien Hero n'est pas confirmée.
// En attendant le formulaire de devis définitif, les deux actions
// conduisent à la page Contact existante. Ne pas simuler un formulaire.
const QUOTE_HREF = "/contact";
const EXPERT_HREF = "/contact";

const CONTENT = {
  fr: {
    eyebrow: "TÉLÉALARME ISS · COMMUNICATION D’URGENCE POUR ASCENSEURS",
    titleFirst: "Une communication d’urgence claire.",
    titleSecond: "Une téléalarme prête pour les réseaux d’aujourd’hui.",
    description:
      "Modernisez la communication d’urgence de vos ascenseurs avec une solution 4G/IP pensée pour l’installation et le suivi de vos équipements.",
    quote: "Demander un devis",
    expert: "Parler à un expert",
    note: "Étude selon vos équipements et votre configuration.",
    imageAlt:
      "Présentation des équipements de téléalarme ISS : interface d'appel et matériels de communication.",
    imageCaption: "Visuel matériel ISS · références et configurations à confirmer",
    proofOne: "Pour les professionnels de l’ascenseur",
    proofTwo: "Communication 4G/IP selon configuration",
    proofThree: "Intégration étudiée avant déploiement",
    ariaActions: "Demander un devis ou contacter un expert Téléalarme ISS"
  },
  en: {
    eyebrow: "ISS EMERGENCY CALL SYSTEM · ELEVATOR EMERGENCY COMMUNICATION",
    titleFirst: "Clear emergency communication.",
    titleSecond: "An elevator alarm system ready for today's networks.",
    description:
      "Modernize your elevators' emergency communication with a 4G/IP solution designed around installation needs and equipment monitoring.",
    quote: "Request a quote",
    expert: "Talk to an expert",
    note: "Subject to an assessment of your equipment and configuration.",
    imageAlt:
      "ISS elevator emergency-call equipment, including the in-car call interface and communications hardware.",
    imageCaption: "ISS product visual · exact models and configurations to be confirmed",
    proofOne: "For elevator industry professionals",
    proofTwo: "4G/IP communications depending on configuration",
    proofThree: "Integration assessment before deployment",
    ariaActions: "Request a quotation or contact an ISS emergency call expert"
  }
};

/**
 * Identify a real touch tablet in landscape orientation. Geometry alone would
 * also target desktop browsers; pointer-only media queries may miss iPads
 * when a trackpad is connected. This extra class is limited to the ISS Hero.
 */
function isTabletLandscapeViewport() {
  if (typeof window === "undefined") return false;

  const width = window.innerWidth;
  const height = window.innerHeight;
  const hasTouch =
    (typeof navigator !== "undefined" && navigator.maxTouchPoints > 0) ||
    (typeof window.matchMedia === "function" &&
      window.matchMedia("(any-pointer: coarse)").matches);

  return hasTouch && width >= 768 && width <= 1366 &&
    height >= 500 && height <= 1050 && width > height;
}

export default function HeroTelealarmeISS() {
  const [tabletLandscape, setTabletLandscape] = React.useState(isTabletLandscapeViewport);

  React.useEffect(() => {
    const refresh = () => setTabletLandscape(isTabletLandscapeViewport());
    window.addEventListener("resize", refresh);
    window.addEventListener("orientationchange", refresh);
    refresh();
    return () => {
      window.removeEventListener("resize", refresh);
      window.removeEventListener("orientationchange", refresh);
    };
  }, []);

  const { i18n } = useTranslation();
  const language = (i18n.resolvedLanguage || i18n.language || "fr")
    .split("-")[0]
    .toLowerCase();
  const text = CONTENT[language] || CONTENT.fr;

  // Données structurées minimales. Pas de fabricant, norme,
  // compatibilité ou garantie inventés.
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "Téléalarme ISS",
    description: text.description
  };

  return (
    <section className={tabletLandscape ? "hero-iss hero-iss--tablet-landscape" : "hero-iss"} aria-labelledby="iss-title">
      <div className="iss-hero__container">
        <div className="iss-hero__grid">
          <div className="iss-hero__copy">
            <p className="iss-hero__eyebrow">{text.eyebrow}</p>
            <h1 id="iss-title" className="iss-hero__title">
              <span className="iss-hero__title-main">{text.titleFirst}</span>
              <span className="iss-hero__title-accent">{text.titleSecond}</span>
            </h1>
            <p className="iss-hero__description">{text.description}</p>

            <div className="iss-hero__actions" role="group" aria-label={text.ariaActions}>
              <a className="iss-hero__button iss-hero__button--primary" href={QUOTE_HREF}>
                {text.quote}
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
                  <path d="M5 12h14m-6-6 6 6-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </a>
              <a className="iss-hero__button iss-hero__button--secondary" href={EXPERT_HREF}>
                {text.expert}
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
                  <path d="M7 17 17 7M8 7h9v9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </a>
            </div>
            <p className="iss-hero__microcopy">{text.note}</p>
          </div>

          <figure className="iss-hero__visual">
            <div className="iss-hero__visual-frame">
              <img
                className="iss-hero__product-image"
                src={PRODUCT_IMAGE}
                alt={text.imageAlt}
                loading="eager"
                decoding="async"
              />
            </div>
            <figcaption className="iss-hero__caption">{text.imageCaption}</figcaption>
          </figure>
        </div>

        <ul className="iss-hero__trust" aria-label={language === "en" ? "Key information" : "Informations essentielles"}>
          <li>{text.proofOne}</li>
          <li>{text.proofTwo}</li>
          <li>{text.proofThree}</li>
        </ul>
      </div>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
    </section>
  );
}
